import React, { useState, useEffect } from 'react';
import { Download, Laptop, Check, X, Smartphone, Sparkles } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PwaInstallButtonProps {
  className?: string;
  variant?: 'compact' | 'full' | 'banner';
}

export const PwaInstallButton: React.FC<PwaInstallButtonProps> = ({
  className = '',
  variant = 'compact'
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  useEffect(() => {
    // Check if running as installed standalone PWA
    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      setIsInstalled(isStandalone);
    };

    checkStandalone();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowGuideModal(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          setIsInstalled(true);
          setDeferredPrompt(null);
        }
      } catch (err) {
        console.warn('PWA install prompt error:', err);
      }
    } else {
      // If browser already shows it in address bar or requires manual click
      setShowGuideModal(true);
    }
  };

  // If already installed and running standalone, do not show button
  if (isInstalled) {
    return null;
  }

  return (
    <>
      {variant === 'banner' ? (
        <div className={`p-3 rounded-2xl bg-gradient-to-r from-blue-600/15 via-indigo-600/15 to-purple-600/15 border border-indigo-500/25 flex items-center justify-between gap-3 ${className}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0">
              <Laptop className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-bold text-brand-text flex items-center gap-1.5">
                <span>Install MeetSplit Guest App</span>
                <span className="px-1.5 py-0.2 rounded bg-indigo-500/20 text-indigo-400 text-[10px] font-mono">1-CLICK</span>
              </p>
              <p className="text-[11px] text-brand-muted">
                Run as a standalone desktop app with zero lag & instant room access.
              </p>
            </div>
          </div>

          <button
            onClick={handleInstallClick}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95 cursor-pointer shrink-0"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Install App</span>
          </button>
        </div>
      ) : variant === 'full' ? (
        <button
          onClick={handleInstallClick}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95 cursor-pointer ${className}`}
        >
          <Download className="w-4 h-4" />
          <span>Install Guest App</span>
        </button>
      ) : (
        <button
          onClick={handleInstallClick}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-xs font-semibold transition-all active:scale-95 cursor-pointer ${className}`}
          title="Install as Chrome/Edge Desktop App"
        >
          <Download className="w-3.5 h-3.5 text-indigo-400" />
          <span>Install App</span>
        </button>
      )}

      {/* Guide Modal when browser install icon is in URL bar */}
      {showGuideModal && (
        <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-3xl bg-brand-surface border border-brand-border shadow-elevated p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-brand-border/60">
              <div className="flex items-center gap-2 text-indigo-400">
                <Laptop className="w-5 h-5" />
                <h3 className="text-sm font-bold text-brand-text">Install MeetSplit App</h3>
              </div>
              <button
                onClick={() => setShowGuideModal(false)}
                className="p-1 rounded-lg bg-brand-elevated text-brand-muted hover:text-brand-text cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs text-brand-muted leading-relaxed">
              <div className="p-3 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-brand-text space-y-1">
                <p className="font-bold flex items-center gap-1.5 text-indigo-300">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Google Chrome / Edge Desktop:</span>
                </p>
                <p className="text-[11px] text-brand-muted">
                  Look at the top-right of your browser's address bar (URL bar). Click the <strong>Install icon ( 🖥️ )</strong> or click the 3-dots menu → <strong>"Install MeetSplit AI Guest"</strong>.
                </p>
              </div>

              <div className="p-3 rounded-2xl bg-brand-elevated/60 border border-brand-border/40 text-brand-text space-y-1">
                <p className="font-bold flex items-center gap-1.5 text-brand-text">
                  <Smartphone className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Mobile (Android / iPhone):</span>
                </p>
                <p className="text-[11px] text-brand-muted">
                  Tap your browser menu (3-dots or Share button) and select <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong>.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowGuideModal(false)}
              className="w-full py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
